import * as React from "react";
import { DayPicker } from "react-day-picker";

import { ChevronDown, ChevronLeft, ChevronRight } from "@/assets/icons";
import { generateValidClassNameHandler } from "@/lib/utils";
import { buttonVariants } from "@/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>

export const Calendar = ({
    captionLayout,
    className,
    classNames,
    fromYear,
    showOutsideDays = true,
    toYear,
    ...props
}: CalendarProps) => (
    <DayPicker
        captionLayout={captionLayout}
        fromYear={fromYear}
        showOutsideDays={showOutsideDays}
        toDate={new Date()}
        toYear={toYear}
        className={generateValidClassNameHandler(
            "p-3",
            className,
        )}
        classNames={{
            caption: "flex justify-center pt-1 pb-2 relative items-center",
            caption_dropdowns: "flex justify-center gap-2 items-center", // eslint-disable-line
            caption_label: "text-xs md:text-sm font-medium", // eslint-disable-line
            cell: "h-9 w-9 text-center text-xs md:text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r [&:has([aria-selected].day-outside)]:bg-gray-100/50 [&:has([aria-selected])]:bg-gray-100 first:[&:has([aria-selected])]:rounded-l last:[&:has([aria-selected])]:rounded-r focus-within:relative focus-within:z-20",
            day: generateValidClassNameHandler(
                buttonVariants({ variant: "ghost" }),
                "h-9 w-9 !py-2 !px-4 font-normal aria-selected:opacity-100 hover:bg-primary hover:text-primary-foreground",
            ),
            day_disabled: "text-gray-500 opacity-50", // eslint-disable-line
            day_hidden: "invisible", // eslint-disable-line
            day_outside: "day-outside text-gray-500 aria-selected:bg-gray-100/50", // eslint-disable-line
            day_range_end: "day-range-end", // eslint-disable-line
            day_range_middle:  "aria-selected:bg-gray-200 aria-selected:text-foreground", // eslint-disable-line
            day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground", // eslint-disable-line
            day_today: "bg-gray-200 text-foreground", // eslint-disable-line
            dropdown: "rounded-md border border-gray-300 bg-white text-xs font-medium shadow-sm",
            dropdown_month: "rounded-md border border-gray-300 bg-white text-xs font-medium shadow-sm", // eslint-disable-line
            dropdown_year: "rounded-md border border-gray-300 bg-white text-xs font-medium shadow-sm", // eslint-disable-line
            head_cell: "text-gray-500 rounded w-9 font-normal text-mini", // eslint-disable-line
            head_row: "flex", // eslint-disable-line
            month: "space-y-4",
            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            nav: "space-x-1 flex items-center",
            nav_button: generateValidClassNameHandler( // eslint-disable-line
                buttonVariants({ variant: "outline" }),
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
            ),
            nav_button_next: "absolute right-1", // eslint-disable-line
            nav_button_previous: "absolute left-1", // eslint-disable-line
            row: "flex w-full mt-2",
            table: "w-full border-collapse space-y-1",
            ...classNames,
        }}
        components={{
            IconLeft: ({ // eslint-disable-line
                className, // eslint-disable-line
                ...props // eslint-disable-line
            }) => (
                <ChevronLeft
                    className={generateValidClassNameHandler(className)}
                    size={16}
                    {...props}
                />
            ),
            IconRight: ({ // eslint-disable-line
                className, // eslint-disable-line
                ...props // eslint-disable-line
            }) => (
                <ChevronRight
                    className={generateValidClassNameHandler(className)}
                    size={16}
                    {...props}
                />
            ),
            Dropdown: ({ // eslint-disable-line
                children,
                className: dropdownClassName,
                ...dropdownProps
            }) => (
                <div className="relative">
                    <select
                        className={generateValidClassNameHandler(
                            "appearance-none inline-flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-1 pr-8 text-xs font-medium shadow-sm hover:bg-gray-50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary min-w-[80px] cursor-pointer",
                            dropdownClassName,
                        )}
                        {...dropdownProps}
                    >
                        {children}
                    </select>
                    <ChevronDown
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                        size={12}
                    />
                </div>
            ),
        }}
        {...props}
    />
);

Calendar.displayName = "Calendar";
